import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { api, formatGBP } from '@/lib/api';
import useSEO from '@/lib/useSEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Check, ChevronLeft, ChevronRight, Upload, X, ImageIcon, Plus, Minus, Printer } from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
    { key: 'size', label: 'Size' },
    { key: 'frame', label: 'Framing' },
    { key: 'prints', label: 'Add prints' },
    { key: 'upload', label: 'Upload' },
    { key: 'notes', label: 'Notes' },
    { key: 'delivery', label: 'Delivery' },
    { key: 'review', label: 'Review & Pay' },
];

function Stepper({ step }) {
    const pct = ((step + 1) / STEPS.length) * 100;
    return (
        <div data-testid="commission-stepper">
            <div className="flex items-center justify-between mb-3">
                <p className="caption">Step {step + 1} of {STEPS.length}</p>
                <p className="caption text-[color:var(--umber)]">{STEPS[step].label}</p>
            </div>
            <Progress value={pct} className="h-1 bg-[color:var(--paper-2)]" data-testid="commission-progress-bar" />
            <div className="mt-4 flex flex-wrap gap-2">
                {STEPS.map((s, i) => (
                    <span
                        key={s.key}
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 sm:px-3 py-1 text-[11px] sm:text-xs border ${
                            i < step
                                ? 'bg-[color:var(--paper-2)] border-[color:var(--border)] text-[color:var(--graphite)]'
                                : i === step
                                  ? 'bg-[color:var(--paper)] border-[color:var(--ring)] text-[color:var(--umber)]'
                                  : 'bg-transparent border-[color:var(--border)] text-[color:var(--muted-fg)]'
                        }`}
                    >
                        {i < step && <Check className="h-3 w-3" />} {i + 1}. {s.label}
                    </span>
                ))}
            </div>
        </div>
    );
}

function computeTotal(config, selection, printAddons) {
    if (!config) return { size: null, frame: null, delivery: null, total: 0, printsSubtotal: 0, prints: [] };
    const size = config.sizes.find((s) => s.id === selection.size_id);
    const frame = config.frames.find((f) => f.id === selection.frame_id);
    const delivery = config.deliveries.find((d) => d.id === selection.delivery_id);
    const prints = printAddons
        .filter((a) => a.quantity > 0)
        .map((a) => {
            const p = config.sizes.find((s) => s.id === a.size_id);
            return p ? { ...a, size: p, line_total: p.price * a.quantity } : null;
        })
        .filter(Boolean);
    const printsSubtotal = prints.reduce((sum, p) => sum + p.line_total, 0);
    const total = (size?.price || 0) + (frame?.price || 0) + (delivery?.price || 0) + printsSubtotal;
    return { size, frame, delivery, prints, printsSubtotal, total };
}

function Summary({ totals, files }) {
    const { size, frame, delivery, prints, printsSubtotal, total } = totals;
    return (
        <div className="rounded-2xl bg-[color:var(--paper)] border border-[color:var(--linen)] p-5 sm:p-6 shadow-[0_12px_34px_rgba(31,27,24,0.06)]" data-testid="commission-price-summary">
            <p className="caption mb-4">Your commission</p>
            <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                    <span className="text-[color:var(--graphite)]">Hand-drawn portrait</span>
                    <span className="text-[color:var(--umber)] text-right">{size ? size.label : '—'}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                    <span className="text-[color:var(--graphite)]">Framing</span>
                    <span className="text-[color:var(--umber)] text-right">{frame ? frame.label : '—'}</span>
                </div>
                {prints.length > 0 && (
                    <div className="pt-2 border-t border-dashed border-[color:var(--border)]">
                        <p className="caption mb-2">Print add-ons</p>
                        {prints.map((p) => (
                            <div key={p.size_id} className="flex items-center justify-between gap-3 text-sm">
                                <span className="text-[color:var(--graphite)]">{p.size.label} × {p.quantity}</span>
                                <span className="text-[color:var(--umber)]">{formatGBP(p.line_total)}</span>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex items-center justify-between gap-3">
                    <span className="text-[color:var(--graphite)]">Delivery</span>
                    <span className="text-[color:var(--umber)] text-right">{delivery ? delivery.label : '—'}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                    <span className="text-[color:var(--graphite)]">Reference photos</span>
                    <span className="text-[color:var(--umber)]">{files.length}</span>
                </div>
                <div className="border-t border-[color:var(--border)] pt-3 flex items-center justify-between">
                    <span className="text-[color:var(--umber)] font-medium">Total</span>
                    <span className="text-[color:var(--umber)] font-display text-lg" data-testid="summary-total">{formatGBP(total)}</span>
                </div>
            </div>
            <p className="text-xs text-[color:var(--muted-fg)] mt-4 leading-relaxed">
                You'll be redirected to Stripe for secure payment. Leiha will be in touch by email once the commission begins.
            </p>
        </div>
    );
}

export default function Commission() {
    const location = useLocation();
    useSEO({
        title: 'Commission a portrait',
        description: 'Begin your bespoke animal portrait. Choose size, framing, optionally add fine-art prints, upload your reference photograph and pay securely with Stripe.',
        path: '/commission',
    });

    const [config, setConfig] = useState(null);
    const [step, setStep] = useState(0);
    const [selection, setSelection] = useState({ size_id: '', frame_id: '', delivery_id: '' });
    const [printAddons, setPrintAddons] = useState([]); // [{size_id, quantity}]
    const [files, setFiles] = useState([]);
    const [notes, setNotes] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [customer, setCustomer] = useState({
        name: '', email: '', phone: '', address_line1: '', address_line2: '', city: '', postcode: '', country: 'United Kingdom',
    });
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get('/config').then((r) => {
            setConfig(r.data);
            const originalSizes = r.data.sizes.filter((s) => (s.product_type || 'original') === 'original');
            const printSizes = r.data.sizes.filter((s) => (s.product_type || 'original') === 'print');
            const defaultSize = originalSizes.find((s) => s.name === 'A4') || originalSizes[0];
            const unframed = r.data.frames.find((f) => f.name === 'unframed') || r.data.frames[0];
            const standardDel = r.data.deliveries.find((d) => d.name === 'uk_standard') || r.data.deliveries[0];
            setSelection({
                size_id: defaultSize ? defaultSize.id : '',
                frame_id: unframed ? unframed.id : '',
                delivery_id: standardDel ? standardDel.id : '',
            });
            // Pre-seed print addons at quantity 0 so we can show steppers
            setPrintAddons(printSizes.map((s) => ({ size_id: s.id, quantity: 0 })));
        });
    }, []);

    useEffect(() => {
        const q = new URLSearchParams(location.search);
        if (q.get('cancelled')) {
            toast.info('Payment cancelled. You can review and try again.');
        }
    }, [location.search]);

    const originalSizes = useMemo(
        () => (config ? config.sizes.filter((s) => (s.product_type || 'original') === 'original') : []),
        [config],
    );
    const printSizes = useMemo(
        () => (config ? config.sizes.filter((s) => (s.product_type || 'original') === 'print') : []),
        [config],
    );

    const totals = useMemo(() => computeTotal(config, selection, printAddons), [config, selection, printAddons]);

    function setSelect(key, value) {
        setSelection((prev) => ({ ...prev, [key]: value }));
    }

    function changePrintQty(size_id, delta) {
        setPrintAddons((prev) =>
            prev.map((a) =>
                a.size_id === size_id
                    ? { ...a, quantity: Math.max(0, Math.min(20, a.quantity + delta)) }
                    : a,
            ),
        );
    }

    function handleFiles(fileList) {
        const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];
        const next = [];
        for (const f of fileList) {
            if (!allowed.includes(f.type)) {
                toast.error(`Unsupported file type: ${f.name}`);
                continue;
            }
            if (f.size > 12 * 1024 * 1024) {
                toast.error(`${f.name} is larger than 12 MB`);
                continue;
            }
            next.push({ file: f, preview: URL.createObjectURL(f) });
        }
        setFiles((prev) => [...prev, ...next].slice(0, 6));
        if (next.length > 0) toast.success(next.length === 1 ? 'Photo added' : `${next.length} photos added`);
    }

    function removeFile(idx) {
        setFiles((prev) => prev.filter((_, i) => i !== idx));
    }

    function next() {
        setError(null);
        // Step 0=size, 1=frame, 2=prints (optional), 3=upload, 4=notes, 5=delivery, 6=review
        if (step === 0 && !selection.size_id) return setError('Please choose a size.');
        if (step === 1 && !selection.frame_id) return setError('Please choose a framing option.');
        if (step === 3 && files.length === 0) return setError('Please upload at least one reference photo.');
        if (step === 5) {
            if (!customer.name || !customer.email || !customer.address_line1 || !customer.city || !customer.postcode) {
                return setError('Please complete your delivery details.');
            }
            if (!selection.delivery_id) return setError('Please choose a delivery option.');
        }
        setStep((s) => Math.min(STEPS.length - 1, s + 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function back() {
        setError(null);
        setStep((s) => Math.max(0, s - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    async function submit() {
        if (submitting) return;
        if (!acceptedTerms) {
            setError('Please accept the order policy, terms and privacy policy to continue.');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const filteredAddons = printAddons.filter((a) => a.quantity > 0);
            const orderRes = await api.post('/orders', {
                selection,
                print_addons: filteredAddons,
                notes,
                customer,
                accepted_terms: acceptedTerms,
            });
            const order = orderRes.data;

            const formData = new FormData();
            for (const f of files) formData.append('files', f.file);
            await api.post(`/orders/${order.id}/images`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const origin = window.location.origin;
            const sessRes = await api.post('/checkout/session', { order_id: order.id, origin_url: origin });

            window.location.href = sessRes.data.url;
        } catch (e) {
            console.error(e);
            const msg = e?.response?.data?.detail || 'Something went wrong. Please try again.';
            setError(typeof msg === 'string' ? msg : 'Something went wrong.');
            toast.error(typeof msg === 'string' ? msg : 'Something went wrong.');
            setSubmitting(false);
        }
    }

    if (!config) {
        return <div className="py-20 text-center text-[color:var(--muted-fg)]" data-testid="loading-state">Loading…</div>;
    }

    return (
        <div className="py-10 sm:py-16 safe-bottom-pad">
            <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mb-7 sm:mb-8">
                    <p className="caption mb-3">Commission</p>
                    <h1 className="font-display text-3xl sm:text-5xl text-[color:var(--umber)] leading-tight">Begin your portrait.</h1>
                    <p className="mt-4 text-base text-[color:var(--muted-fg)] leading-relaxed">
                        Seven small steps. Take your time — you can return and continue at any point before payment.
                    </p>
                </div>

                <Stepper step={step} />

                <div className="mt-8 sm:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                    <div className="lg:col-span-7">
                        {error && (
                            <Alert className="mb-6 border-[color:var(--border)] bg-[color:var(--paper-2)]" data-testid="commission-step-error">
                                <AlertDescription className="text-sm text-[#B42318]">{error}</AlertDescription>
                            </Alert>
                        )}

                        {step === 0 && (
                            <div data-testid="commission-step-size">
                                <h2 className="font-display text-2xl text-[color:var(--umber)] mb-2">Choose your size</h2>
                                <p className="text-sm text-[color:var(--muted-fg)] mb-6">Each portrait is hand-drawn on archival paper at standard frame sizes.</p>
                                <RadioGroup value={selection.size_id} onValueChange={(v) => setSelect('size_id', v)} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {originalSizes.map((s) => (
                                        <Label key={s.id} htmlFor={`size-${s.id}`} className={`cursor-pointer rounded-2xl border p-4 sm:p-5 flex items-start gap-3 transition-colors ${selection.size_id === s.id ? 'border-[color:var(--ring)] bg-[color:var(--paper)]' : 'border-[color:var(--border)] bg-[color:var(--paper-2)]'}`}>
                                            <RadioGroupItem id={`size-${s.id}`} value={s.id} data-testid={`size-radio-${s.name}`} />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-3">
                                                    <p className="font-display text-base sm:text-lg text-[color:var(--umber)] truncate">{s.label}</p>
                                                    <p className="text-sm text-[color:var(--umber)] whitespace-nowrap">{formatGBP(s.price)}</p>
                                                </div>
                                                {s.description && <p className="text-sm text-[color:var(--muted-fg)] mt-1">{s.description}</p>}
                                            </div>
                                        </Label>
                                    ))}
                                </RadioGroup>
                            </div>
                        )}

                        {step === 1 && (
                            <div data-testid="commission-step-framing">
                                <h2 className="font-display text-2xl text-[color:var(--umber)] mb-2">Choose your framing</h2>
                                <p className="text-sm text-[color:var(--muted-fg)] mb-6">Available unframed or with a standard or premium frame.</p>
                                <RadioGroup value={selection.frame_id} onValueChange={(v) => setSelect('frame_id', v)} className="grid grid-cols-1 gap-3">
                                    {config.frames.map((f) => (
                                        <Label key={f.id} htmlFor={`frame-${f.id}`} className={`cursor-pointer rounded-2xl border p-4 sm:p-5 flex items-start gap-3 transition-colors ${selection.frame_id === f.id ? 'border-[color:var(--ring)] bg-[color:var(--paper)]' : 'border-[color:var(--border)] bg-[color:var(--paper-2)]'}`}>
                                            <RadioGroupItem id={`frame-${f.id}`} value={f.id} data-testid={`frame-radio-${f.name}`} />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-3">
                                                    <p className="font-display text-base sm:text-lg text-[color:var(--umber)]">{f.label}</p>
                                                    <p className="text-sm text-[color:var(--umber)] whitespace-nowrap">{f.price === 0 ? 'No extra charge' : `+ ${formatGBP(f.price)}`}</p>
                                                </div>
                                                {f.description && <p className="text-sm text-[color:var(--muted-fg)] mt-1">{f.description}</p>}
                                            </div>
                                        </Label>
                                    ))}
                                </RadioGroup>
                            </div>
                        )}

                        {step === 2 && (
                            <div data-testid="commission-step-prints">
                                <div className="flex items-center gap-2 mb-2">
                                    <Printer className="h-5 w-5 text-[color:var(--umber)]" />
                                    <h2 className="font-display text-2xl text-[color:var(--umber)]">Add fine-art prints</h2>
                                    <span className="text-xs text-[color:var(--muted-fg)] ml-1">Optional</span>
                                </div>
                                <p className="text-sm text-[color:var(--muted-fg)] mb-6">Order archival giclée prints of your finished portrait, alongside the original. Lovely for a second room, or as gifts.</p>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {printSizes.map((s) => {
                                        const current = printAddons.find((a) => a.size_id === s.id) || { quantity: 0 };
                                        return (
                                            <div key={s.id} className={`rounded-2xl border p-4 sm:p-5 flex items-start justify-between gap-3 ${current.quantity > 0 ? 'border-[color:var(--ring)] bg-[color:var(--paper)]' : 'border-[color:var(--border)] bg-[color:var(--paper-2)]'}`} data-testid={`print-addon-${s.name}`}>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-display text-base sm:text-lg text-[color:var(--umber)]">{s.label}</p>
                                                    <p className="text-sm text-[color:var(--muted-fg)] mt-1">{formatGBP(s.price)} each</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        type="button"
                                                        size="icon"
                                                        variant="outline"
                                                        onClick={() => changePrintQty(s.id, -1)}
                                                        disabled={current.quantity === 0}
                                                        aria-label="Decrease quantity"
                                                        data-testid={`print-addon-minus-${s.name}`}
                                                        className="h-9 w-9 rounded-xl bg-[color:var(--paper)] border-[color:var(--border)] disabled:opacity-40"
                                                    >
                                                        <Minus className="h-4 w-4" />
                                                    </Button>
                                                    <span className="w-6 text-center text-base text-[color:var(--umber)]" data-testid={`print-addon-qty-${s.name}`}>{current.quantity}</span>
                                                    <Button
                                                        type="button"
                                                        size="icon"
                                                        variant="outline"
                                                        onClick={() => changePrintQty(s.id, +1)}
                                                        aria-label="Increase quantity"
                                                        data-testid={`print-addon-plus-${s.name}`}
                                                        className="h-9 w-9 rounded-xl bg-[color:var(--paper)] border-[color:var(--border)]"
                                                    >
                                                        <Plus className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-[color:var(--muted-fg)] mt-5">You can also continue without prints. The original artwork is always included.</p>
                            </div>
                        )}

                        {step === 3 && (
                            <div data-testid="commission-step-upload">
                                <h2 className="font-display text-2xl text-[color:var(--umber)] mb-2">Upload your reference photo</h2>
                                <p className="text-sm text-[color:var(--muted-fg)] mb-2">Please upload a clear image of your animal. <span className="text-[color:var(--umber)] font-medium">High resolution, sharp focus and natural light</span> give the best result.</p>
                                <p className="text-xs text-[color:var(--muted-fg)] mb-5">JPG, PNG or HEIC. Up to 6 photos. 12 MB max per file. See our <Link to="/order-policy" className="under-link">order policy</Link>.</p>

                                <label
                                    htmlFor="file-input"
                                    onDragOver={(e) => { e.preventDefault(); }}
                                    onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
                                    className="relative rounded-2xl border border-dashed border-[color:var(--border)] bg-[color:var(--paper)] p-8 hover:bg-[color:var(--paper-2)] transition-colors block text-center cursor-pointer"
                                    data-testid="commission-upload-dropzone"
                                >
                                    <Upload className="h-6 w-6 mx-auto text-[color:var(--graphite)]" />
                                    <p className="mt-2 text-sm text-[color:var(--umber)]">Drag photos here, or tap to choose</p>
                                    <p className="mt-1 text-xs text-[color:var(--muted-fg)]">We recommend a sharp, well-lit, front-facing photograph.</p>
                                    <input
                                        id="file-input"
                                        type="file"
                                        accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
                                        multiple
                                        className="hidden"
                                        data-testid="commission-upload-file-input"
                                        onChange={(e) => handleFiles(e.target.files)}
                                    />
                                </label>

                                {files.length > 0 && (
                                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="commission-upload-preview">
                                        {files.map((f, i) => (
                                            <div key={i} className="relative rounded-xl overflow-hidden border border-[color:var(--border)] bg-[color:var(--paper-2)] aspect-square">
                                                <img src={f.preview} alt={f.file.name} className="w-full h-full object-cover" />
                                                <button
                                                    type="button"
                                                    onClick={() => removeFile(i)}
                                                    aria-label="Remove image"
                                                    className="absolute top-2 right-2 bg-[color:var(--paper)]/95 border border-[color:var(--border)] rounded-full p-1.5 hover:bg-[color:var(--paper-2)]"
                                                    data-testid={`commission-upload-remove-${i}`}
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {step === 4 && (
                            <div data-testid="commission-step-notes">
                                <h2 className="font-display text-2xl text-[color:var(--umber)] mb-2">Notes about the portrait</h2>
                                <p className="text-sm text-[color:var(--muted-fg)] mb-5">Tell Leiha anything that will help — your animal's name, a particular pose, markings to highlight, or if this is a gift with a deadline.</p>
                                <Textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="E.g. This is for my mother's 60th in March. Her dog Sunny is gentle and soulful — please capture his eyes."
                                    className="min-h-[180px] bg-[color:var(--paper)] border-[color:var(--border)]"
                                    data-testid="commission-notes-input"
                                />
                            </div>
                        )}

                        {step === 5 && (
                            <div data-testid="commission-step-delivery">
                                <h2 className="font-display text-2xl text-[color:var(--umber)] mb-2">Delivery & details</h2>
                                <p className="text-sm text-[color:var(--muted-fg)] mb-6">We'll only use these details to deliver your order and keep you updated.</p>

                                <div className="space-y-3 mb-8">
                                    <p className="caption">Delivery option</p>
                                    <RadioGroup value={selection.delivery_id} onValueChange={(v) => setSelect('delivery_id', v)} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {config.deliveries.map((d) => (
                                            <Label key={d.id} htmlFor={`del-${d.id}`} className={`cursor-pointer rounded-2xl border p-4 flex items-start gap-3 transition-colors ${selection.delivery_id === d.id ? 'border-[color:var(--ring)] bg-[color:var(--paper)]' : 'border-[color:var(--border)] bg-[color:var(--paper-2)]'}`}>
                                                <RadioGroupItem id={`del-${d.id}`} value={d.id} data-testid={`delivery-radio-${d.name}`} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <p className="text-sm text-[color:var(--umber)] font-medium">{d.label}</p>
                                                        <p className="text-sm text-[color:var(--umber)] whitespace-nowrap">{d.price === 0 ? 'Free' : formatGBP(d.price)}</p>
                                                    </div>
                                                    {d.description && <p className="text-xs text-[color:var(--muted-fg)] mt-1">{d.description}</p>}
                                                </div>
                                            </Label>
                                        ))}
                                    </RadioGroup>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="c-name">Full name</Label>
                                        <Input id="c-name" data-testid="customer-name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)] h-11" />
                                    </div>
                                    <div>
                                        <Label htmlFor="c-email">Email</Label>
                                        <Input id="c-email" type="email" data-testid="customer-email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)] h-11" />
                                    </div>
                                    <div>
                                        <Label htmlFor="c-phone">Phone (optional)</Label>
                                        <Input id="c-phone" data-testid="customer-phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)] h-11" />
                                    </div>
                                    <div>
                                        <Label htmlFor="c-country">Country</Label>
                                        <Input id="c-country" data-testid="customer-country" value={customer.country} onChange={(e) => setCustomer({ ...customer, country: e.target.value })} className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)] h-11" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <Label htmlFor="c-a1">Address line 1</Label>
                                        <Input id="c-a1" data-testid="customer-address1" value={customer.address_line1} onChange={(e) => setCustomer({ ...customer, address_line1: e.target.value })} className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)] h-11" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <Label htmlFor="c-a2">Address line 2 (optional)</Label>
                                        <Input id="c-a2" data-testid="customer-address2" value={customer.address_line2} onChange={(e) => setCustomer({ ...customer, address_line2: e.target.value })} className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)] h-11" />
                                    </div>
                                    <div>
                                        <Label htmlFor="c-city">City</Label>
                                        <Input id="c-city" data-testid="customer-city" value={customer.city} onChange={(e) => setCustomer({ ...customer, city: e.target.value })} className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)] h-11" />
                                    </div>
                                    <div>
                                        <Label htmlFor="c-pc">Postcode</Label>
                                        <Input id="c-pc" data-testid="customer-postcode" value={customer.postcode} onChange={(e) => setCustomer({ ...customer, postcode: e.target.value })} className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)] h-11" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 6 && (
                            <div data-testid="commission-step-review">
                                <h2 className="font-display text-2xl text-[color:var(--umber)] mb-2">Review your order</h2>
                                <p className="text-sm text-[color:var(--muted-fg)] mb-6">Once you're happy, you'll be taken to Stripe to pay securely.</p>
                                <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--paper-2)] p-5 space-y-4">
                                    <div>
                                        <p className="caption mb-2">Reference photos</p>
                                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                            {files.map((f, i) => (
                                                <div key={i} className="aspect-square rounded-lg overflow-hidden border border-[color:var(--border)] bg-[color:var(--paper)]">
                                                    <img src={f.preview} alt="" className="w-full h-full object-cover" />
                                                </div>
                                            ))}
                                            {files.length === 0 && (
                                                <div className="col-span-full text-sm text-[color:var(--muted-fg)] flex items-center gap-2">
                                                    <ImageIcon className="h-4 w-4" /> No reference photos yet
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="caption mb-2">Notes</p>
                                        <p className="text-sm text-[color:var(--graphite)] whitespace-pre-line">{notes || 'None provided.'}</p>
                                    </div>
                                    <div>
                                        <p className="caption mb-2">Deliver to</p>
                                        <p className="text-sm text-[color:var(--graphite)]">
                                            {customer.name}<br />
                                            {customer.address_line1}{customer.address_line2 ? `, ${customer.address_line2}` : ''}<br />
                                            {customer.city}, {customer.postcode}<br />
                                            {customer.country}<br />
                                            {customer.email} {customer.phone && `· ${customer.phone}`}
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-6 rounded-2xl border border-[color:var(--border)] bg-[color:var(--paper)] p-5">
                                    <label className="flex items-start gap-3 cursor-pointer" htmlFor="accept-terms">
                                        <Checkbox id="accept-terms" checked={acceptedTerms} onCheckedChange={(v) => setAcceptedTerms(!!v)} data-testid="commission-accept-terms" className="mt-1" />
                                        <span className="text-sm text-[color:var(--graphite)] leading-relaxed">
                                            I confirm that my reference photograph is of high quality, that I have read and accept the{' '}
                                            <Link to="/order-policy" target="_blank" rel="noreferrer" className="under-link text-[color:var(--umber)]">order &amp; commission policy</Link>,{' '}
                                            <Link to="/terms" target="_blank" rel="noreferrer" className="under-link text-[color:var(--umber)]">terms &amp; conditions</Link> and{' '}
                                            <Link to="/privacy" target="_blank" rel="noreferrer" className="under-link text-[color:var(--umber)]">privacy policy</Link>.{' '}
                                            I understand that original artwork is hand-drawn and non-refundable once work has begun.
                                        </span>
                                    </label>
                                </div>
                            </div>
                        )}

                        <div className="mt-8 sm:mt-10 hidden lg:flex items-center justify-between">
                            <Button
                                variant="outline"
                                onClick={back}
                                disabled={step === 0 || submitting}
                                data-testid="commission-back-button"
                                className="rounded-xl bg-[color:var(--paper-2)] border-[color:var(--border)] text-[color:var(--umber)] hover:bg-[color:var(--paper)]"
                            >
                                <ChevronLeft className="h-4 w-4 mr-1" /> Back
                            </Button>
                            {step < STEPS.length - 1 ? (
                                <Button
                                    onClick={next}
                                    data-testid="commission-next-button"
                                    className="rounded-xl bg-[color:var(--umber)] text-[color:var(--paper)] hover:bg-[color:var(--ink)]"
                                >
                                    Continue <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            ) : (
                                <Button
                                    onClick={submit}
                                    disabled={submitting || !acceptedTerms}
                                    data-testid="commission-pay-button"
                                    className="rounded-xl bg-[color:var(--terra)] text-white hover:bg-[#B84F33] px-6 disabled:opacity-60"
                                >
                                    {submitting ? 'Preparing…' : `Pay ${formatGBP(totals.total)} securely`}
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="hidden lg:block lg:sticky lg:top-24">
                            <Summary totals={totals} files={files} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile sticky bottom bar */}
            <div className="lg:hidden fixed left-0 right-0 bottom-0 z-30 border-t border-[color:var(--border)] bg-[color:var(--paper)]/95 backdrop-blur" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
                <div className="mx-auto w-full max-w-6xl px-4 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-[10px] caption">Total</p>
                        <p className="font-display text-lg text-[color:var(--umber)] leading-none mt-1" data-testid="mobile-summary-total">{formatGBP(totals.total)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            onClick={back}
                            disabled={step === 0 || submitting}
                            data-testid="commission-back-button-mobile"
                            className="h-11 rounded-xl bg-[color:var(--paper-2)] border-[color:var(--border)] text-[color:var(--umber)] px-3"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        {step < STEPS.length - 1 ? (
                            <Button
                                onClick={next}
                                data-testid="commission-next-button-mobile"
                                className="h-11 rounded-xl bg-[color:var(--umber)] text-[color:var(--paper)] hover:bg-[color:var(--ink)] px-4"
                            >
                                Continue <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                        ) : (
                            <Button
                                onClick={submit}
                                disabled={submitting || !acceptedTerms}
                                data-testid="commission-pay-button-mobile"
                                className="h-11 rounded-xl bg-[color:var(--terra)] text-white hover:bg-[#B84F33] px-4 disabled:opacity-60"
                            >
                                {submitting ? 'Preparing…' : 'Pay securely'}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
